import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useDispatch } from 'react-redux';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { AvatarLarge, IconSpinner } from '../../../components';
import { addToFavorites } from '../../LandingPage/LandingPage.duck';
import { ICONS } from '../../ProviderPage/components/ProfilePage/Icons';
import { useConfiguration } from '../../../context/configurationContext';

import css from './UserCard.module.css';

const UserCard = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    rootClassName,
    className,
    user,
    currentUser,
    onContactUser,
    listing,
    sessionCount,
    sessionTypes = [],
    rating: propRating,
    reviewCount: propReviewCount,
  } = props;

  const dispatch = useDispatch();
  const config = useConfiguration();
  const reviewCount = propReviewCount ?? 0;
  const computedRating = propRating ?? 0;

  // Session types store category/subcategory as raw ids (e.g. "sports",
  // "basketball" -- see CreateSessionTypeForm.js's Select `value={c.id}`),
  // not display names. Flatten config.categoryConfiguration.categories into
  // an id -> name lookup (walking subcategories, however deep they nest) so
  // the tags below show "Basketball", not "basketball".
  const categoryNameById = useMemo(() => {
    const map = new Map();
    const walk = cats => {
      (cats || []).forEach(cat => {
        map.set(cat.id, cat.name);
        walk(cat.subcategories);
      });
    };
    walk(config.categoryConfiguration.categories);
    return map;
  }, [config.categoryConfiguration.categories]);


  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;

  const [isFavorite, setIsFavorite] = useState(false);
  const [loader, setLoader] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentUser?.attributes?.profile?.publicData) {
      const favoritesListing = currentUser.attributes.profile.publicData.favoritesCoach || [];
      const id = ensuredUser.id?.uuid || ensuredUser.id;
      const isListingAlreadyExist = favoritesListing.includes(id);
      setIsFavorite(isListingAlreadyExist);
    }
  }, [currentUser, ensuredUser.id]);

  const handleFavToggle = async event => {
    event.preventDefault();
    event.stopPropagation();
    if (!currentUser) return;

    setLoader(true);
    try {
      const res = await dispatch(
        addToFavorites({
          listingId: ensuredUser.id.uuid,
          currentUserId: currentUser?.id?.uuid,
          type: 'user',
        })
      );
      if (res) setIsFavorite(res.isFavorites);
    } catch (e) {
      console.error('Error toggling favorite:', e);
    } finally {
      setLoader(false);
    }
  };

  const profileAttributes = ensuredUser.attributes.profile || {};
  const { displayName } = profileAttributes;
  const publicData = profileAttributes.publicData || {};
  const socialsArray = publicData.socials || [];
  const providerSocials = socialsArray.length > 0
    ? socialsArray.filter(s => s.enabled)
    : [
      publicData.instagram && {
        id: 'ig',
        platform: 'instagram',
        url: publicData.instagram.startsWith('http')
          ? publicData.instagram
          : `https://instagram.com/${publicData.instagram.replace('@', '')}`,
        enabled: true,
      },
      publicData.youtube && {
        id: 'yt',
        platform: 'youtube',
        url: publicData.youtube.startsWith('http')
          ? publicData.youtube
          : `https://youtube.com/${publicData.youtube.startsWith('@') ? publicData.youtube : '@' + publicData.youtube}`,
        enabled: true,
      },
      publicData.twitter && {
        id: 'tw',
        platform: 'twitter',
        url: publicData.twitter.startsWith('http')
          ? publicData.twitter
          : `https://x.com/${publicData.twitter.replace('@', '')}`,
        enabled: true,
      },
      publicData.facebook && {
        id: 'fb',
        platform: 'facebook',
        url: publicData.facebook.startsWith('http')
          ? publicData.facebook
          : `https://facebook.com/${publicData.facebook.replace('@', '')}`,
        enabled: true,
      },
      publicData.tiktok && {
        id: 'tt',
        platform: 'tiktok',
        url: publicData.tiktok.startsWith('http')
          ? publicData.tiktok
          : `https://tiktok.com/@${publicData.tiktok.replace('@', '')}`,
        enabled: true,
      },
    ].filter(Boolean);


  // Dynamic values from publicData
  const locationData = publicData.location || (listing?.attributes?.publicData?.location) || 'Location not specified';
  const location = typeof locationData === 'object'
    ? (locationData.address || locationData.building || 'Location not specified')
    : locationData;
  const coachTitle = publicData.coachTitle || 'Certified Coach';

  // Rating and reviews - computed from reviews fetched by author ID
  const rating = computedRating;
  // reviewCount is already defined from useSelector above

  // Dynamic stats
  const activeClients = publicData.stats?.activeClients || publicData.clientCount || 0;
  const sessionsValue = sessionCount !== undefined ? sessionCount : (publicData.stats?.totalSessions || '0');

  const stats = [
    { value: sessionsValue, label: 'Sessions' },
    { value: activeClients, label: 'Clients' },
    { value: rating > 0 ? rating : '-', label: 'Rating' },
  ];

  /**
   * Build deduplicated tags from session types.
   * Prefer session-level mainCategory / subCategory.
   * Fall back to listing-level publicData values if sessions have no category data.
   */
  const tags = useMemo(() => {
    // Resolve a stored category/subcategory id (or, for older/fallback
    // data, an already-human string) to its display name.
    const resolveCategoryTag = value => {
      if (!value) return null;
      const trimmed = value.trim();
      return trimmed ? categoryNameById.get(trimmed) || trimmed : null;
    };

    const tagSet = new Set();

    if (sessionTypes.length > 0) {
      sessionTypes.forEach(st => {
        if (st.mainCategory) tagSet.add(resolveCategoryTag(st.mainCategory));
        if (st.subCategory)  tagSet.add(resolveCategoryTag(st.subCategory));
        // Also include top-level category for sessions that only have that
        if (!st.mainCategory && !st.subCategory && st.category) {
          tagSet.add(resolveCategoryTag(st.category));
        }
      });
    }

    // Fallback: use listing-level publicData when session types carry no category info
    if (tagSet.size === 0) {
      if (publicData.serviceCategory)    tagSet.add(resolveCategoryTag(publicData.serviceCategory));
      if (publicData.serviceSubCategory) tagSet.add(resolveCategoryTag(publicData.serviceSubCategory));
    }

    return Array.from(tagSet).filter(Boolean);
  }, [sessionTypes, publicData.serviceCategory, publicData.serviceSubCategory, categoryNameById]);


  // Pro status from publicData
  const isPro = !!publicData.isPro;

  // Response time from publicData
  const responseTime = publicData.responseTime || 'Usually responds within a few hours';

  const history = useHistory();
  const handleContactUserClick = () => {
    if (!currentUser) {
      onContactUser(user);
      return;
    }
    const customerId = ensuredUser.id?.uuid || ensuredUser.id;
    history.push(`/messages/${customerId}`);
  };

  const handleDonateClick = () => {
    const coachId = ensuredUser.id?.uuid || ensuredUser.id;
    history.push(`/u/${coachId}?section=donations`);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const getSocialIcon = (platform, className) => {
    const p = (platform || '').toLowerCase();
    const icon = ICONS[p];
    if (!icon) return null;
    return React.cloneElement(icon, {
      className,
      width: 16,
      height: 16,
    });
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.providerCard}>
        <div className={css.backgroundBlob} />

        <div className={css.providerHeader}>
          <div className={css.providerHeaderLeft}>
            <div className={css.avatarWrapper}>
              <AvatarLarge className={css.avatarLarge} user={user} />
              <div className={css.checkBadge}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>

            <div className={css.providerInfo}>
              <div className={css.nameRow}>
                <h2 className={css.providerName}>{displayName}</h2>
                {isPro && <span className={css.proBadge}>Pro Coach</span>}
              </div>

              <p className={css.providerTitle}>{coachTitle}</p>

              <div className={css.providerMeta}>
                {reviewCount > 0 ? (
                  <div className={css.metaItem}>
                    <svg
                      className={css.starIcon}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span className={css.metaValueBlack}>{rating}</span>
                    <span className={css.metaValueGray}>({reviewCount} reviews)</span>
                  </div>
                ) : (
                  <div className={css.metaItem}>
                    <svg
                      className={css.starIcon}
                      style={{ color: '#9ca3af' }}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    <span className={css.metaValueGray}>No reviews yet</span>
                  </div>
                )}
                <div className={css.metaItem}>
                  <svg
                    className={css.locationIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span className={css.metaValueGray}>{location}</span>
                </div>
                <div className={css.metaItem}>
                  <svg
                    className={css.clockIcon}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  <span className={css.metaValueGray}>{responseTime}</span>
                </div>
              </div>

              <div className={css.tagsContainer}>
                {tags.map((tag, i) => (
                  <span key={i} className={css.tag}>
                    {tag}
                  </span>
                ))}
              </div>

              <div className={css.iconButtonsRow}>
                {providerSocials.map(social => {
                  const platform = (social.platform || '').toLowerCase();
                  const icon = getSocialIcon(platform, css[`icon${platform}`]);
                  if (!icon) return null;

                  return (
                    <a
                      key={social.id}
                      href={social.url}
                      target="_blank"
                      rel="noreferrer"
                      className={classNames(css.actionButtonSquare, css[`button${platform}`])}
                      aria-label={social.platform}
                    >
                      {icon}
                    </a>
                  );
                })}

                {!isCurrentUser && (
                  <>
                    <button className={css.actionButtonPrimary} onClick={handleContactUserClick}>
                      <svg
                        className={css.iconMessage}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="16"
                        height="16"
                      >
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                      </svg>
                      Message
                    </button>
                    <button className={css.actionButtonDonate} onClick={handleDonateClick}>
                      <svg
                        className={css.iconGift}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="16"
                        height="16"
                      >
                        <polyline points="20 12 20 22 4 22 4 12" />
                        <rect x="2" y="7" width="20" height="5" />
                        <line x1="12" y1="22" x2="12" y2="7" />
                        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C9 2 12 7 12 7z" />
                        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C15 2 12 7 12 7z" />
                      </svg>
                      Donate
                    </button>
                    <button
                      className={classNames(css.actionButtonSquare, { [css.favoriteActive]: isFavorite })}
                      onClick={handleFavToggle}
                      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {loader ? (
                        <IconSpinner className={css.spinnerCustom} />
                      ) : (
                        <svg
                          className={css.iconHeart}
                          viewBox="0 0 24 24"
                          fill={isFavorite ? 'currentColor' : 'none'}
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          width="16"
                          height="16"
                        >
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      )}
                    </button>
                    <div className={css.shareContainer}>
                      <button
                        className={css.actionButtonSquare}
                        aria-label="Share"
                        onClick={handleShare}
                      >
                        <svg
                          className={css.iconShare}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          width="16"
                          height="16"
                        >
                          <circle cx="18" cy="5" r="3" />
                          <circle cx="6" cy="12" r="3" />
                          <circle cx="18" cy="19" r="3" />
                          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                        </svg>
                      </button>
                      {copied && <div className={css.tooltip}>Copied!</div>}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className={css.providerHeaderRight}>
            <div className={css.verticalDivider}>
              {stats.map((stat, i) => (
                <div key={i} className={css.statItem}>
                  <p className={css.statValue}>{stat.value}</p>
                  <p className={css.statLabel}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCard;
